// "Needs attention" block (Round 10, 3A): a purely deterministic
// re-surfacing of EXISTING states — no AI, no new judgments. Sources:
// commitment rows (date-passed / pending-soon), the date-review record
// count, and unaddressed QA/export-gate findings. Each item is one line,
// tappable to its section or panel.

import type { CommitmentRow } from "./commitments";
import { displayFactTitle, formatFactDate } from "./factMeta";

export interface AttentionItem {
  kind: "date_passed" | "deadline_soon" | "needs_review" | "qa_flags";
  label: string;
  // Overview section id to jump to, when the target is a section.
  targetId?: string;
}

export const DEADLINE_SOON_DAYS = 7;

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function buildNeedsAttention(opts: {
  commitmentRows: CommitmentRow[];
  needsReviewCount: number;
  qaFlagCount: number;
  todayIso: string;
}): AttentionItem[] {
  const items: AttentionItem[] = [];

  // 1. Date-passed, unresolved (resolved rows carry state "resolved" and
  //    are already out). One line: count + top 3 by recency.
  const passed = opts.commitmentRows
    .filter((r) => r.state === "date_passed")
    .sort((a, b) => ((a.fact.deadline ?? "") < (b.fact.deadline ?? "") ? 1 : -1));
  if (passed.length > 0) {
    const top = passed
      .slice(0, 3)
      .map((r) => displayFactTitle(r.fact))
      .join(" · ");
    items.push({
      kind: "date_passed",
      label: `${passed.length} now overdue: ${top}`,
      targetId: "ov-commitments",
    });
  }

  // 2. Pending deadlines within the next N days — one line each.
  const horizon = addDays(opts.todayIso, DEADLINE_SOON_DAYS);
  const soon = opts.commitmentRows
    .filter(
      (r) =>
        r.state === "pending" &&
        !!r.fact.deadline &&
        r.fact.deadline.slice(0, 10) <= horizon,
    )
    .sort((a, b) => (a.fact.deadline! < b.fact.deadline! ? -1 : 1));
  for (const r of soon) {
    const party = r.fact.party ? `${r.fact.party} — ` : "";
    items.push({
      kind: "deadline_soon",
      label: `Due ${formatFactDate(r.fact.deadline!.slice(0, 10))}: ${party}${displayFactTitle(r.fact)}`,
      targetId: "ov-commitments",
    });
  }

  // 3. Records flagged "need review" (date extraction fallback).
  if (opts.needsReviewCount > 0) {
    items.push({
      kind: "needs_review",
      label: `${opts.needsReviewCount} record${opts.needsReviewCount === 1 ? "" : "s"} with a date the app wasn’t sure about — check if it matters`,
    });
  }

  // 4. QA / export-gate findings still unaddressed. The label names the
  // destination so the tap is never surprising (Round 10.1, 2C).
  if (opts.qaFlagCount > 0) {
    items.push({
      kind: "qa_flags",
      label: `${opts.qaFlagCount} check${opts.qaFlagCount === 1 ? "" : "s"} to review before you can export`,
    });
  }

  return items;
}

// Section order for the Overview (Round 10, Part 3) — action above
// reference. The nav derives from this constant; a test pins the order.
// Parties are NOT a section anymore (chip row under the case header).
export const OVERVIEW_SECTION_ORDER: ReadonlyArray<{ id: string; label: string }> = [
  { id: "ov-attention", label: "Needs attention" },
  { id: "ov-status", label: "Current status" },
  { id: "ov-financial", label: "Financial" },
  { id: "ov-commitments", label: "Commitments" },
  { id: "ov-chronology", label: "Chronology" },
  { id: "ov-coverage", label: "Coverage" },
  { id: "ov-valuation", label: "Valuation" },
];
